import { useCallback, useContext, useMemo, useRef } from "react";
import { ReconciliationSummaryResponse } from "@common/contracts/reconciliation.contract";
import { withoutCashItemsClearedByStatementLines } from "@common/helpers/reconciliationCashItems";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { FinancialAccount } from "@common/types/financialAccount";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { ViewConfig } from "@lib/views";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import {
    DEFAULT_FILTERS,
    FiltersChangeFn,
} from "@components/transactions/filters/lib";
import { TransactionSortValue } from "@components/transactions/useSort";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { ReconciliationContext } from "./Reconciliation.context";
import { ReconciliationClearActionCell } from "./ReconciliationClearActionCell";
import { ReconciliationClearCashItemsContext } from "./ReconciliationClearCashItems.context";
import { ReconciliationFindCheckMatch } from "./ReconciliationFindCheckMatch";
import { ReconciliationFinishButton } from "./ReconciliationFinishButton";
import { ReconciliationCloseButton } from "./ReconciliationCloseButton";
import {
    clampClearStepDates,
    createReconciliationClearView,
} from "./createReconciliationClearView";
import { RECONCILIATION_PRIMARY_HOTKEY } from "./helpers";
import { useFinishReconciliation } from "./useFinishReconciliation";
import { useReconciliationBalances } from "./useReconciliationBalances";
import { getStatementTransactionsToAdd } from "./workflowHelpers";
import styles from "./Reconciliation.module.scss";

export interface ReconciliationClearStepContentProps {
    financialAccount: FinancialAccount;
    summary: ReconciliationSummaryResponse;
    previousClosingDate?: string;
    onBack: () => void;
    onHide: () => void;
    onSuccess?: () => void;
    defaultView: ReturnType<typeof createReconciliationClearView>;
}

export const ReconciliationClearStepContent: React.FC<
    ReconciliationClearStepContentProps
> = ({
    financialAccount,
    summary,
    previousClosingDate,
    onBack,
    onHide,
    onSuccess,
    defaultView,
}) => {
    const {
        isReconciliationComplete,
        statementTransactionsToAddIdsSet,
        checkMerges,
    } = useContext(ReconciliationContext);
    const { isBalanced } = useReconciliationBalances({
        summary,
        financialAccount,
    });
    const { finish, isFinishing } = useFinishReconciliation({
        pairs: summary.transactionsPairs,
        financialAccount,
    });
    const statementLinesToAdd = useMemo(
        () =>
            getStatementTransactionsToAdd({
                pairs: summary.transactionsPairs,
                statementTransactionsToAddIdsSet,
            }),
        [summary.transactionsPairs, statementTransactionsToAddIdsSet],
    );
    const cashItemsToClear = withoutCashItemsClearedByStatementLines(summary);
    const cashItemsByTransactionId = new Map(
        cashItemsToClear.map((item) => [item.financialTransactionId, item]),
    );

    const { filters, currentView, transactions } = useContext(
        PaginatedTransactionsDataContext,
    );
    const firstOutstandingTransactionId = transactions?.find(({ id }) =>
        checkMerges.every(
            ({ checkTransactionId }) => checkTransactionId !== id,
        ),
    )?.id;
    const {
        setCurrentFilters,
        setPage,
        setShownTransactionId,
        setCurrentView,
    } = useContext(PaginatedTransactionsActionsContext);

    const transactionsViewConfig = useTransactionViewConfig();
    const viewConfigOverride = useMemo<
        ViewConfig<TransactionListItem, string, TransactionSortValue>
    >(
        () => ({
            ...transactionsViewConfig,
            columns: {
                ...transactionsViewConfig.columns,
                [TransactionColumn.CTA]: {
                    ...transactionsViewConfig.columns[TransactionColumn.CTA],
                    header: "Action",
                    cell: ReconciliationClearActionCell,
                    minSize: 140,
                    maxSize: 160,
                },
            },
        }),
        [transactionsViewConfig],
    );

    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const merged = { ...currentFiltersRef.current, ...value };
            const newFilters = clampClearStepDates({
                filters: omitBy(merged, (v) => v === null || v === undefined),
                statementsClosingDate: summary.statementsClosingDate,
            });
            if (isEqual(newFilters, currentFiltersRef.current)) {
                return;
            }
            setPage(1);
            setShownTransactionId(undefined);
            setCurrentFilters(newFilters);
        },
        [
            setPage,
            setShownTransactionId,
            setCurrentFilters,
            summary.statementsClosingDate,
        ],
    );

    const availableFilters = useMemo(
        () => [
            FilterTabs.DATE,
            FilterTabs.CATEGORY,
            FilterTabs.AMOUNT,
            FilterTabs.COUNTERPARTY,
            FilterTabs.NOTE,
            FilterTabs.CASH_ITEM_TYPE,
        ],
        [],
    );

    const handleViewChange = useCallback(
        (view: typeof currentView) => setCurrentView(view),
        [setCurrentView],
    );

    return (
        <ReconciliationClearCashItemsContext.Provider
            value={{ cashItemsByTransactionId, firstOutstandingTransactionId }}
        >
            <div className={styles.workflowContainer}>
                <div className={styles.workflowScrollbarsContainer}>
                    <div className={styles.clearStepContent}>
                        <TablePageHeader
                            search={filters.search}
                            onSearchChange={(value) => {
                                handleFiltersChange({ search: value ?? "" });
                            }}
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            defaultFilters={DEFAULT_FILTERS}
                            hideClearButton
                            availableFilters={availableFilters}
                            isFullPage
                        >
                            <ColumnSettings
                                view={currentView}
                                onViewChange={handleViewChange}
                                viewConfig={viewConfigOverride}
                                defaultView={defaultView}
                            />
                        </TablePageHeader>
                        <div className={styles.clearStepTable}>
                            <TransactionsView
                                view={currentView}
                                viewConfigOverride={viewConfigOverride}
                                emptyView={() => null}
                                footer={() => null}
                            />
                        </div>
                    </div>
                </div>
                <CommandIsland sticky alignEnd>
                    {isReconciliationComplete ? (
                        <ReconciliationCloseButton
                            onHide={onHide}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <>
                            {!isBalanced ? (
                                <span className={styles.balanceMismatch}>
                                    Kick balance does not match the statement.
                                    Go back to match bank transactions.
                                </span>
                            ) : null}
                            <Button
                                variant="primary"
                                size="md"
                                onClick={onBack}
                            >
                                Back
                            </Button>
                            <ReconciliationFinishButton
                                variant="primary"
                                outstandingCashItems={cashItemsToClear}
                                onFinish={finish}
                                isFinishing={isFinishing}
                                disabled={!isBalanced}
                                hotKey={RECONCILIATION_PRIMARY_HOTKEY}
                            />
                        </>
                    )}
                </CommandIsland>
                <ReconciliationFindCheckMatch
                    financialAccount={financialAccount}
                    summary={summary}
                    previousClosingDate={previousClosingDate}
                    statementLinesToAdd={statementLinesToAdd}
                />
            </div>
        </ReconciliationClearCashItemsContext.Provider>
    );
};
